import { useCallback, useEffect, useState } from "react";
import type { User } from "firebase/auth";

import { callAuthenticatedWordinariusFunction } from "../platform/firebaseFunctions";

export interface WebMonetizationProfile {
  subscriptionTier: "free" | "premium";
  adsEnabled: boolean;
  premiumStatus: string;
  premiumBillingProvider: string | null;
  entitlementExpiresAt: string | null;
  source: "guest" | "backend";
}

interface MonetizationResponse {
  subscriptionTier?: unknown;
  adsEnabled?: unknown;
  premiumStatus?: unknown;
  premiumBillingProvider?: unknown;
  entitlementExpiresAt?: unknown;
}

export const GUEST_WEB_MONETIZATION_PROFILE: WebMonetizationProfile = {
  subscriptionTier: "free",
  adsEnabled: true,
  premiumStatus: "inactive",
  premiumBillingProvider: null,
  entitlementExpiresAt: null,
  source: "guest",
};

export function resolveWebMonetizationProfile(value?: MonetizationResponse | null): WebMonetizationProfile {
  if (!value) {
    return GUEST_WEB_MONETIZATION_PROFILE;
  }
  const premium = value.subscriptionTier === "premium";
  return {
    subscriptionTier: premium ? "premium" : "free",
    adsEnabled: typeof value.adsEnabled === "boolean" ? value.adsEnabled : !premium,
    premiumStatus: typeof value.premiumStatus === "string" && value.premiumStatus.trim()
      ? value.premiumStatus.trim()
      : "inactive",
    premiumBillingProvider: typeof value.premiumBillingProvider === "string" && value.premiumBillingProvider.trim()
      ? value.premiumBillingProvider.trim()
      : null,
    entitlementExpiresAt: typeof value.entitlementExpiresAt === "string" && value.entitlementExpiresAt.trim()
      ? value.entitlementExpiresAt.trim()
      : null,
    source: "backend",
  };
}

export function useWebMonetization(user: User | null) {
  const [profile, setProfile] = useState<WebMonetizationProfile>(GUEST_WEB_MONETIZATION_PROFILE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile(GUEST_WEB_MONETIZATION_PROFILE);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await callAuthenticatedWordinariusFunction<MonetizationResponse>(
        "refreshUserMonetizationProfile",
        {},
      );
      setProfile(resolveWebMonetizationProfile(response));
    } catch {
      setError("premium_refresh_failed");
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { error, loading, profile, refresh };
}
